import api from "../../../core/api";
import { GuestPass, IssueGuestPass } from "../models/guestPassModels";

// Guest passes live on the ACCOUNT service (apiBaseUrl) — NOT the Customers
// proxy that user CRUD uses. Same base-URL pattern as customerUsersApi's
// getAccountRequestConfig (roles / change-role).
const getAccountRequestConfig = () => {
  return { baseURL: (window as any).app.config.apiBaseUrl };
};

class GuestPassesApi {
  issue(model: IssueGuestPass): Promise<GuestPass> {
    return api
      .post<GuestPass>(`/v1/users/guest-passes`, model, getAccountRequestConfig())
      .then((x) => x.data);
  }

  // DEMO_USER_TYPE_SPEC B4/F1 — Admin-only demo mint. Same request body as
  // `issue`; the server creates (or reuses) a Demo-access-type, Worker-role user
  // and refuses LOUDLY when the caller's company is not demo-allowlisted. That
  // refusal message is surfaced VERBATIM in the dialog (spec §3 point 1), so this
  // method deliberately does no error mapping.
  issueDemo(model: IssueGuestPass): Promise<GuestPass> {
    return api
      .post<GuestPass>(`/v1/users/guest-passes/demo`, model, getAccountRequestConfig())
      .then((x) => x.data);
  }

  list(machineInternalNumber?: string): Promise<GuestPass[]> {
    return api
      .get<GuestPass[]>(`/v1/users/guest-passes`, {
        ...getAccountRequestConfig(),
        params: machineInternalNumber ? { machineInternalNumber } : undefined,
      })
      .then((x) => x.data);
  }

  revoke(id: string): Promise<void> {
    return api
      .post<void>(`/v1/users/guest-passes/${id}/revoke`, null, getAccountRequestConfig())
      .then(() => undefined);
  }

  // TA2 (built in parallel) — POST /v1/users/guest-passes/{id}/reissue mints a
  // fresh pass (new link/PIN) and invalidates the old one immediately; returns
  // the NEW pass row. Coded against the intended contract; endpoint may lag.
  reissue(id: string): Promise<GuestPass> {
    return api
      .post<GuestPass>(`/v1/users/guest-passes/${id}/reissue`, null, getAccountRequestConfig())
      .then((x) => x.data);
  }

  // GP-MENU §3.2 — "Nový PIN". Reissue mechanics server-side (fresh secrets, new
  // row, old row revoked FIRST) whose response carries the new PIN ONCE. NO email
  // on any source: the screen is the channel. Two sibling routes so the auth gates
  // stay declarative attributes — guest = Manager+, demo = Admin-only — and the
  // server refuses a cross-route id with 1210 (SEC-REQ GP-1). Callers pick the
  // route from the ROW's `source`; never guess.
  //
  // Show-once: the returned pass may carry `pinCode`. It is held in Redux only
  // while the handoff dialog is open (`lastRegeneratedPass`) and is NEVER merged
  // into the list array — see guestPassesSlice.
  regeneratePin(id: string): Promise<GuestPass> {
    return api
      .post<GuestPass>(
        `/v1/users/guest-passes/${id}/regenerate-pin`,
        null,
        getAccountRequestConfig()
      )
      .then((x) => x.data);
  }

  regenerateDemoPin(id: string): Promise<GuestPass> {
    return api
      .post<GuestPass>(
        `/v1/users/guest-passes/demo/${id}/regenerate-pin`,
        null,
        getAccountRequestConfig()
      )
      .then((x) => x.data);
  }
}

export default new GuestPassesApi();
